'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import type { ActivityItem } from '@/lib/api/history/models';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { Link } from '@/lib/routing';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { parseUtcIso } from '@/lib/utils/dates';
import { Spinner } from '../ui-kit/spinner';
import { Text } from '../ui-kit/text';
import { cn } from '../ui-kit/utils';
import { Empty } from './Empty';
import { RelativeDateText } from './Text/RelativeDateText';

interface ActivityCardProps {
  items: ActivityItem[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const ActivityCard = ({
  items,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ActivityCardProps) => {
  const t = useTranslations('activity');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size={36} className="!text-v2-text-secondary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Empty title={t('empty.title')} desc={t('empty.description')} />
      </div>
    );
  }

  return (
    <>
      {items.map((item) => (
        <div
          key={item.transactionId}
          className="flex w-full items-start gap-3 border-b border-v2-border py-4"
        >
          <UserAvatar
            userId={item.userId}
            name={item.username}
            look={item.look}
            size={36}
            className="border border-solid border-v2-border"
          />
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Link
                  href={`/stats/profile/${item.userId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-fit max-w-full"
                >
                  <Text variant="body-small" className="block truncate text-txt-1 hover:underline">
                    <GlowName glow={item.look?.glow ?? null}>{item.username}</GlowName>
                  </Text>
                </Link>
                <Text variant="body-small" className="text-txt-1">
                  {item.side?.toLowerCase()}{' '}
                  <span
                    className={cn(
                      'font-semibold',
                      item.position?.toLowerCase() === 'yes' ? 'text-success' : 'text-red-500',
                    )}
                  >
                    {item.position}
                  </span>
                  <Text as="span" variant="body-small" className="text-txt-3">
                    {' '}
                    ({item.shares} {t('shares')})
                  </Text>
                </Text>
                <Text variant="body-small" className="line-clamp-2 text-txt-3">
                  {item.outcomeName}
                </Text>
              </div>
              <RelativeDateText
                date={parseUtcIso(item.executedAt)}
                formatStyle="compact"
                variant="body-small"
                className="shrink-0 whitespace-nowrap text-txt-3"
              />
            </div>
          </div>
        </div>
      ))}
      <div ref={loadMoreRef} className="h-1" />
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Spinner size={24} />
        </div>
      )}
    </>
  );
};
